/**
 * Fitxes tècniques de proveïdor, fase 1 (Linear OBR-41) — la part PURA (sense I/O, sense rellotge implícit).
 *
 * QUÈ ÉS. La FT del proveïdor és un document de CADA RELACIÓ article-proveïdor (`supplier_articles`): no té res a
 * veure amb la fitxa tècnica pròpia del producte elaborat (`tech_sheets`, que ha de dir el que l'obrador fabrica).
 * Aquí hi ha el que la pantalla de l'article, la vista de «FT pendents» i la funció del servidor comparteixen: en
 * quin estat és una relació, a qui es demana, què es pot adjuntar i com s'agrupen els pendents.
 *
 * L'ESTAT ES DERIVA, MAI ES DESA. Hi ha fitxer = «Vigent»; si no, hi ha una petició que cobreix la parella
 * (proveïdor, article) = «Sol·licitada»; si no, «Sense FT». Un estat desat es desincronitzaria del registre de
 * peticions (que és append-only); el derivat no pot.
 *
 * LA FUNCIÓ DEL SERVIDOR EN PORTA UNA CÒPIA. `backend/functions/supplier-ft-request.ts` (Deno: no pot importar
 * aquest paquet) repeteix la regla del destinatari, els límits de text i la lectura del correu general, marcats
 * «Mirrors of @obrava/shared». `src/edge/supplierFtRequestMirror.test.ts` les lliga: si en canvies una, el test
 * falla fins que canviïs l'altra.
 */

import type { UserRole } from '../enums/index.js';
import { isEmailAddress } from './purchaseOrderDispatch.js';

// ── Qui ──────────────────────────────────────────────────────────────────────────────────

/**
 * Qui pot SOL·LICITAR una FT (funció `supplier-ft-request`): admin, compres (els qui editen la relació article-
 * proveïdor) i qualitat (la propietària dels documents del proveïdor, i el destinatari és la seva bústia de qualitat).
 */
export const FT_REQUEST_ROLES = [
  'admin',
  'purchasing',
  'quality',
] as const satisfies readonly UserRole[];

/**
 * Qui pot ADJUNTAR una FT (RPC `attach_supplier_tech_sheet`): admin i compres, que són els qui escriuen a
 * `supplier_articles` i poden pujar al bucket `article-documents`.
 */
export const FT_ATTACH_ROLES = ['admin', 'purchasing'] as const satisfies readonly UserRole[];

// ── Límits (el servidor i la base de dades els repeteixen) ───────────────────────────────

/** Màxim de caràcters de l'assumpte (CHECK de `supplier_ft_requests.subject`). */
export const FT_SUBJECT_MAX_LENGTH = 250;
/** Màxim de caràcters del text editable (CHECK de `supplier_ft_requests.body`). */
export const FT_BODY_MAX_LENGTH = 5000;
/** Màxim de caràcters de la versió del document (CHECK de `supplier_articles.tech_sheet_version`). */
export const FT_VERSION_MAX_LENGTH = 60;
/** Màxim d'articles d'una sola petició (el mateix que accepta `record_supplier_ft_request`). */
export const FT_REQUEST_MAX_ARTICLES = 200;
/**
 * Límits d'ús del remitent compartit (la funció `supplier-ft-request` envia text lliure amb el nom d'Obrava): a un
 * proveïdor no se li torna a escriure fins passats `FT_REQUEST_COOLDOWN_SECONDS` de l'última petició, i un obrador no en
 * fa més de `FT_REQUEST_DAILY_LIMIT` cada `FT_REQUEST_DAILY_WINDOW_SECONDS` segons. Els aplica el servidor (429).
 */
export const FT_REQUEST_COOLDOWN_SECONDS = 600;
export const FT_REQUEST_DAILY_LIMIT = 20;
export const FT_REQUEST_DAILY_WINDOW_SECONDS = 86_400;

// ── Estat ────────────────────────────────────────────────────────────────────────────────

/** Els tres estats d'una FT de proveïdor, en l'ordre en què es mostren. */
export const SUPPLIER_TECH_SHEET_STATUSES = ['none', 'requested', 'valid'] as const;
export type SupplierTechSheetStatus = (typeof SUPPLIER_TECH_SHEET_STATUSES)[number];

/** Cert si el text és un fitxer (una url no buida). */
export function hasTechSheetFile(url: string | null | undefined): boolean {
  return typeof url === 'string' && url.trim() !== '';
}

/**
 * L'estat d'una relació: `valid` si hi ha fitxer; si no, `requested` si alguna petició cobreix la parella
 * (proveïdor, article); si no, `none`. L'ordre és el que importa: un fitxer adjuntat guanya sempre a una petició
 * (després de demanar-la i rebre-la, la relació és vigent, i la petició és història).
 */
export function supplierTechSheetStatus(input: {
  readonly techSheetUrl: string | null | undefined;
  /** Hi ha almenys una petició que cobreix aquesta parella (proveïdor, article). */
  readonly requested: boolean;
}): SupplierTechSheetStatus {
  if (hasTechSheetFile(input.techSheetUrl)) return 'valid';
  return input.requested ? 'requested' : 'none';
}

/** El que la base de dades sap de la FT d'una relació. */
export interface SupplierTechSheetFacts {
  readonly techSheetUrl: string | null;
  readonly techSheetDate: string | null;
  readonly techSheetVersion: string | null;
  readonly techSheetAttachedAt: string | null;
  readonly techSheetAttachedBy: string | null;
}

export interface SupplierTechSheetSummary {
  readonly status: SupplierTechSheetStatus;
  /** Data DEL DOCUMENT (la que porta el paper), no la d'incorporació. */
  readonly documentDate: string | null;
  readonly version: string | null;
  /** Quan i qui va incorporar el fitxer actual. */
  readonly attachedAt: string | null;
  readonly attachedBy: string | null;
  /**
   * Una FT adjuntada ABANS d'OBR-41 és «Vigent» sense data ni versió (no s'inventa res): la pantalla ho diu i
   * convida a completar-ho. Només té sentit amb estat `valid`.
   */
  readonly legacy: boolean;
}

/** L'estat i les dades visibles d'una relació (les dades només existeixen amb fitxer). */
export function supplierTechSheetSummary(
  facts: SupplierTechSheetFacts,
  requested: boolean,
): SupplierTechSheetSummary {
  const status = supplierTechSheetStatus({ techSheetUrl: facts.techSheetUrl, requested });
  if (status !== 'valid') {
    return {
      status,
      documentDate: null,
      version: null,
      attachedAt: null,
      attachedBy: null,
      legacy: false,
    };
  }
  const version = facts.techSheetVersion?.trim() || null;
  return {
    status,
    documentDate: facts.techSheetDate,
    version,
    attachedAt: facts.techSheetAttachedAt,
    attachedBy: facts.techSheetAttachedBy,
    legacy: facts.techSheetDate === null || version === null || facts.techSheetAttachedAt === null,
  };
}

// ── A qui es demana ──────────────────────────────────────────────────────────────────────

/** D'on surt l'adreça: el correu de qualitat del proveïdor (OBR-125) o, si no n'hi ha, el general. */
export const FT_RECIPIENT_SOURCES = ['quality', 'general'] as const;
export type FtRecipientSource = (typeof FT_RECIPIENT_SOURCES)[number];

export interface FtRecipient {
  readonly email: string;
  readonly source: FtRecipientSource;
}

/**
 * A qui es demana una FT. PRIMER el correu de qualitat del proveïdor; si no n'hi ha (o no és una adreça vàlida),
 * el correu general; si tampoc, `null`: l'usuari rep un avís i NO s'envia res. Les comandes tenen la regla
 * contrària (només el correu de comandes: `orderRecipients`), perquè són bústies amb funcions diferents.
 */
export function ftRecipient(input: {
  readonly qualityEmail?: string | null | undefined;
  readonly generalEmail?: string | null | undefined;
}): FtRecipient | null {
  const quality = (input.qualityEmail ?? '').trim();
  if (quality !== '' && isEmailAddress(quality)) return { email: quality, source: 'quality' };
  const general = (input.generalEmail ?? '').trim();
  if (general !== '' && isEmailAddress(general)) return { email: general, source: 'general' };
  return null;
}

/**
 * El correu general d'un proveïdor: `contact_json.email`. `contact_json` és un objecte lliure (jsonb): qualsevol
 * cosa que no sigui un objecte amb un `email` text dona `null`.
 */
export function supplierGeneralEmail(contactJson: unknown): string | null {
  if (typeof contactJson !== 'object' || contactJson === null || Array.isArray(contactJson))
    return null;
  const email = (contactJson as { readonly email?: unknown }).email;
  return typeof email === 'string' && email.trim() !== '' ? email.trim() : null;
}

// ── Adjuntar ─────────────────────────────────────────────────────────────────────────────

export type TechSheetAttachIssue =
  | 'date_required'
  | 'date_invalid'
  | 'date_future'
  | 'version_required'
  | 'version_too_long';

export type TechSheetAttachment =
  | { readonly ok: true; readonly documentDate: string; readonly version: string }
  | { readonly ok: false; readonly issues: readonly TechSheetAttachIssue[] };

/** Cert si `YYYY-MM-DD` és un dia que existeix (res de 2026-02-31). */
function isCalendarDate(value: string): boolean {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return false;
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day
  );
}

/**
 * Què cal per adjuntar una FT: la data DEL DOCUMENT (que existeixi i no sigui futura) i la versió (no buida, fins
 * a {@link FT_VERSION_MAX_LENGTH}). `today` és `YYYY-MM-DD` i entra per paràmetre (cap rellotge implícit). La base
 * de dades deixa un dia de marge pels fusos horaris; aquí, més estricte: una data d'avui endavant és «futura».
 */
export function validateTechSheetAttachment(input: {
  readonly documentDate: string | null | undefined;
  readonly version: string | null | undefined;
  readonly today: string;
}): TechSheetAttachment {
  const issues: TechSheetAttachIssue[] = [];
  const date = (input.documentDate ?? '').trim();
  if (date === '') issues.push('date_required');
  else if (!isCalendarDate(date)) issues.push('date_invalid');
  else if (date > input.today) issues.push('date_future');
  const version = (input.version ?? '').trim();
  if (version === '') issues.push('version_required');
  else if (version.length > FT_VERSION_MAX_LENGTH) issues.push('version_too_long');
  if (issues.length > 0) return { ok: false, issues };
  return { ok: true, documentDate: date, version };
}

// ── Demanar ──────────────────────────────────────────────────────────────────────────────

export type FtRequestIssue =
  | 'no_recipient'
  | 'no_articles'
  | 'too_many_articles'
  | 'subject_required'
  | 'subject_too_long'
  | 'body_required'
  | 'body_too_long';

export type FtRequestDraftCheck =
  | { readonly ok: true; readonly subject: string; readonly body: string }
  | { readonly ok: false; readonly issues: readonly FtRequestIssue[] };

/**
 * Pot sortir aquesta petició? Cal destinatari (si el proveïdor no té cap correu, no s'envia res), almenys un
 * article i un assumpte i un text que càpiguen. Els mateixos límits que el servidor i la base de dades.
 */
export function validateFtRequest(input: {
  readonly subject: string;
  readonly body: string;
  readonly articleCount: number;
  readonly recipient: FtRecipient | null;
}): FtRequestDraftCheck {
  const issues: FtRequestIssue[] = [];
  if (input.recipient === null) issues.push('no_recipient');
  if (input.articleCount < 1) issues.push('no_articles');
  else if (input.articleCount > FT_REQUEST_MAX_ARTICLES) issues.push('too_many_articles');
  const subject = input.subject.trim();
  if (subject === '') issues.push('subject_required');
  else if (subject.length > FT_SUBJECT_MAX_LENGTH) issues.push('subject_too_long');
  const body = input.body.trim();
  if (body === '') issues.push('body_required');
  else if (body.length > FT_BODY_MAX_LENGTH) issues.push('body_too_long');
  if (issues.length > 0) return { ok: false, issues };
  return { ok: true, subject, body };
}

// ── Límits d'ús ──────────────────────────────────────────────────────────────────────────

/** Una petició del registre, tal com la llegeix la regla dels límits. */
export interface FtRequestWindowEntry {
  readonly id: string;
  readonly supplierId: string;
  readonly requestedAt: string;
}

export type FtRequestLimit =
  | { readonly kind: 'cooldown'; readonly retryAfterSeconds: number }
  | { readonly kind: 'daily'; readonly retryAfterSeconds: number };

/**
 * Pot sortir ara una petició a `supplierId`? Primer el límit per proveïdor (cooldown), després el diari per obrador.
 * `requests` són les peticions de l'obrador (de qualsevol edat: només compten les de les últimes 24 h); es compten per
 * `id`, així una petició amb diverses línies no compta dues vegades. `nowMs` entra per paràmetre (cap rellotge
 * implícit). La funció del servidor en porta una còpia i és qui decideix de debò; la pantalla la fa servir per
 * ensenyar el límit ABANS d'enviar.
 *
 * `retryAfterSeconds` és el que cal esperar perquè deixi de valer (mínim 1).
 */
export function ftRequestLimit(input: {
  readonly requests: readonly FtRequestWindowEntry[];
  readonly supplierId: string;
  readonly nowMs: number;
}): FtRequestLimit | null {
  const cooldownMs = FT_REQUEST_COOLDOWN_SECONDS * 1000;
  const windowMs = FT_REQUEST_DAILY_WINDOW_SECONDS * 1000;
  const seen = new Set<string>();
  const inWindow: { supplierId: string; at: number }[] = [];
  for (const request of input.requests) {
    const at = Date.parse(request.requestedAt);
    if (!Number.isFinite(at) || at <= input.nowMs - windowMs) continue;
    // Una petició amb diverses línies compta una sola vegada (per id), després de mirar si és dins la finestra.
    if (seen.has(request.id)) continue;
    seen.add(request.id);
    inWindow.push({ supplierId: request.supplierId, at });
  }
  let last: number | null = null;
  for (const entry of inWindow) {
    if (entry.supplierId === input.supplierId && (last === null || entry.at > last))
      last = entry.at;
  }
  if (last !== null && last + cooldownMs > input.nowMs) {
    return {
      kind: 'cooldown',
      retryAfterSeconds: Math.max(1, Math.ceil((last + cooldownMs - input.nowMs) / 1000)),
    };
  }
  if (inWindow.length >= FT_REQUEST_DAILY_LIMIT) {
    const ascending = inWindow.map((entry) => entry.at).sort((a, b) => a - b);
    // Quan en surt la que fa baixar el compte per sota del límit.
    const oldestToLeave = ascending[inWindow.length - FT_REQUEST_DAILY_LIMIT] as number;
    return {
      kind: 'daily',
      retryAfterSeconds: Math.max(1, Math.ceil((oldestToLeave + windowMs - input.nowMs) / 1000)),
    };
  }
  return null;
}

// ── Pendents agrupats per proveïdor ──────────────────────────────────────────────────────

/** Una relació article-proveïdor, tal com la llegeix la vista de pendents. */
export interface FtPendingLink {
  readonly id: string;
  readonly supplierId: string;
  readonly articleId: string;
  readonly supplierRef: string | null;
  readonly techSheetUrl: string | null;
  readonly deletedAt: string | null;
}

export interface FtPendingSupplier {
  readonly id: string;
  readonly name: string;
  readonly code: string | null;
  readonly qualityEmail: string | null;
  readonly generalEmail: string | null;
  readonly deletedAt: string | null;
}

export interface FtPendingArticle {
  readonly id: string;
  readonly name: string;
  readonly sku: string | null;
  readonly deletedAt: string | null;
}

/** Una petició que va cobrir la parella (proveïdor, article), amb la data. */
export interface FtRequestMark {
  readonly supplierId: string;
  readonly articleId: string;
  readonly requestedAt: string;
}

export interface FtPendingItem {
  readonly linkId: string;
  readonly articleId: string;
  readonly articleName: string;
  readonly articleSku: string | null;
  readonly supplierRef: string | null;
  /** `none` (mai demanada) o `requested` (ja se n'ha demanat; un recordatori és un gest explícit). */
  readonly status: Exclude<SupplierTechSheetStatus, 'valid'>;
  readonly lastRequestedAt: string | null;
}

export interface FtPendingGroup {
  readonly supplierId: string;
  readonly supplierName: string;
  readonly supplierCode: string | null;
  /** A qui aniria la petició; `null` = el proveïdor no té cap correu i no es pot demanar. */
  readonly recipient: FtRecipient | null;
  readonly items: readonly FtPendingItem[];
  /** Quants articles encara no s'han demanat mai. */
  readonly unrequestedCount: number;
}

/** Ordre de noms sense distingir majúscules ni accents («Àgata» abans de «Bernat»). */
function byName(a: string, b: string): number {
  return a.localeCompare(b, 'ca', { sensitivity: 'base' });
}

/**
 * Les FT que falten, agrupades per proveïdor: per a cadascun, els articles de les seves relacions vives sense fitxer
 * (cap relació, article o proveïdor donat de baixa). Un proveïdor sense pendents no hi surt. Cada article diu si ja
 * se n'ha demanat la FT (`requested`, amb la data de l'última petició) o no (`none`).
 *
 * Els grups van per nom de proveïdor i els articles per nom d'article: la mateixa vista en un moment diferent
 * no es remena.
 */
export function pendingTechSheetsBySupplier(input: {
  readonly links: readonly FtPendingLink[];
  readonly suppliers: readonly FtPendingSupplier[];
  readonly articles: readonly FtPendingArticle[];
  readonly requests: readonly FtRequestMark[];
}): FtPendingGroup[] {
  const suppliers = new Map(
    input.suppliers.filter((s) => s.deletedAt === null).map((s) => [s.id, s]),
  );
  const articles = new Map(
    input.articles.filter((a) => a.deletedAt === null).map((a) => [a.id, a]),
  );
  const lastRequest = new Map<string, string>();
  for (const mark of input.requests) {
    const key = `${mark.supplierId}:${mark.articleId}`;
    const current = lastRequest.get(key);
    if (current === undefined || mark.requestedAt > current) lastRequest.set(key, mark.requestedAt);
  }

  const bySupplier = new Map<string, FtPendingItem[]>();
  const seen = new Set<string>();
  for (const link of input.links) {
    if (link.deletedAt !== null || hasTechSheetFile(link.techSheetUrl)) continue;
    const supplier = suppliers.get(link.supplierId);
    const article = articles.get(link.articleId);
    if (!supplier || !article) continue;
    // Una parella (proveïdor, article) és única entre les vives; per si de cas, una sola vegada.
    const pair = `${link.supplierId}:${link.articleId}`;
    if (seen.has(pair)) continue;
    seen.add(pair);
    const requestedAt = lastRequest.get(pair) ?? null;
    const items = bySupplier.get(supplier.id) ?? [];
    items.push({
      linkId: link.id,
      articleId: article.id,
      articleName: article.name,
      articleSku: article.sku,
      supplierRef: link.supplierRef,
      status: requestedAt === null ? 'none' : 'requested',
      lastRequestedAt: requestedAt,
    });
    bySupplier.set(supplier.id, items);
  }

  return [...bySupplier.entries()]
    .map(([supplierId, items]): FtPendingGroup => {
      const supplier = suppliers.get(supplierId) as FtPendingSupplier;
      const sorted = [...items].sort((a, b) => byName(a.articleName, b.articleName));
      return {
        supplierId,
        supplierName: supplier.name,
        supplierCode: supplier.code,
        recipient: ftRecipient({
          qualityEmail: supplier.qualityEmail,
          generalEmail: supplier.generalEmail,
        }),
        items: sorted,
        unrequestedCount: sorted.filter((i) => i.status === 'none').length,
      };
    })
    .sort((a, b) => byName(a.supplierName, b.supplierName));
}

/**
 * Què es demana per defecte en una petició per proveïdor: els articles que encara no s'han demanat mai. Els ja
 * sol·licitats surten a la llista però sense marcar: tornar-los a demanar (un recordatori) és un gest explícit,
 * no un efecte secundari d'una càrrega massiva.
 */
export function defaultFtSelection(group: FtPendingGroup): string[] {
  return group.items.filter((item) => item.status === 'none').map((item) => item.articleId);
}
